import React, { useState, useContext, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Row,
  Col,
  InputNumber,
  DatePicker,
  Tooltip,
  Switch,
} from "antd";
import dayjs from "dayjs";
import {
  ToolOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  MessageOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import RepairModel from "../lib/firebase/repairModel";
import { trackEvent } from "../helpers/mixpanel";
import { AuthContext } from "../context/AuthProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import unitModel from "../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import HandymanModel from "../lib/firebase/handymanModel";
import styled from "styled-components";
import { useBreakpoints } from "../lib/breakpoints";
import { useNavigate } from "react-router-dom";
import { chatModel } from "../lib/firebase/chatModel";

const { Option } = Select;
const { TextArea } = Input;

function safeToDateValue(dateValue) {
  if (!dateValue) return null;
  if (typeof dateValue.toDate === "function") {
    return dateValue.toDate();
  }
  if (dateValue instanceof Date) {
    return dateValue;
  }
  return new Date(dateValue);
}

function toDayjsValue(value) {
  const d = safeToDateValue(value);
  return d ? dayjs(d) : null;
}

const StyledModal = styled(Modal)`
  .ant-select-arrow {
    top: 50% !important;
    margin-top: 0 !important;
    transform: translateY(-50%) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: auto !important;
    line-height: 1 !important;

    .anticon,
    svg {
      display: block !important;
      margin: 0 !important;
      line-height: 1 !important;
    }
  }

  /* Ensure dropdown options are vertically centered */
  .ant-select-dropdown {
    .ant-select-item {
      display: flex !important;
      align-items: center !important;
      min-height: 32px !important;
      padding: 4px 12px !important;
    }

    .ant-select-item-option-content {
      display: flex !important;
      align-items: center !important;
      line-height: 1.5 !important;
    }
  }

  .ant-modal-header {
    padding: 1.25rem 1.5rem;
    border-bottom: none;
    background: var(--color-primary);
    border-radius: 12px 12px 0 0;
    margin-bottom: 0 !important;
  }
  .ant-modal-title {
    color: #fff;
    font-weight: 600;
  }
  .ant-modal-body {
    padding: 0;
    background: #fff;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    padding: 0 !important;
    max-height: calc(100dvh - 32px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-close {
    color: white !important;
    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }
    &:hover,
    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: #fff !important;
      fill: #fff !important;
    }
  }

  @media (max-width: 768px) {
    .ant-modal-body {
      padding: 0;
    }
    .ant-modal-content {
      margin: 0 0.5rem;
      max-height: calc(100dvh - 24px);
    }
  }
`;

const SectionTitle = styled.h4`
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-border-light);
  display: flex;
  align-items: center;
`;

const InfoIcon = styled(InfoCircleOutlined)`
  color: var(--color-text-muted);
  margin-left: 0.5rem;
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label,
  .ant-form-item-label,
  .ant-form-item-label > label.ant-form-item-required,
  .ant-form-item-label > label.ant-form-item-required::before,
  label {
    color: var(--color-text) !important;
    font-weight: 600 !important;
    font-size: 0.875rem !important;
    font-family: var(--font-primary) !important;
    line-height: 1.4 !important;
    // margin-bottom: 0.5rem !important;
  }

  .ant-form-item-label > label.ant-form-item-required::before {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: #ff4d4f !important;
  }

  .ant-form-item-label > label span {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    font-family: var(--font-primary) !important;
  }

  /* Refined spacing system */
  .ant-form-item {
    margin-top: 0 !important;
    margin-bottom: 0.75rem !important;
  }

  /* Tighter spacing for form rows */
  .ant-row {
    margin-top: 0 !important;
    margin-bottom: 0.5rem !important;
  }

  /* Consistent label spacing */
  .ant-form-item-label {
    margin-bottom: 0rem !important;
  }

  /* Reduce spacing between form items in same row */
  .ant-col + .ant-col .ant-form-item {
    margin-top: 0 !important;
  }

  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-picker {
    border: 2px solid var(--color-border-light);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    height: 44px !important;
    line-height: 1.5;
    display: flex;
    align-items: center;
    font-size: 0.875rem !important;
    font-family: var(--font-primary) !important;
  }

  .ant-select {
    height: 44px !important;
    font-size: 0.875rem !important;

    .ant-select-selector {
      height: 44px !important;
      padding: 0.5rem 0.75rem !important;
      display: flex !important;
      align-items: center !important;
    }

    .ant-select-selection-item {
      line-height: 1.5 !important;
      font-size: 0.875rem !important;
    }

    .ant-select-selection-placeholder {
      line-height: 1.5 !important;
      font-size: 0.875rem !important;
    }
  }

  .ant-picker {
    height: 44px !important;
    width: 100% !important;

    .ant-picker-input {
      height: 44px !important;
      display: flex !important;
      align-items: center !important;
    }

    .ant-picker-input input {
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
    }
  }

  .ant-input-number {
    height: 44px !important;
    width: 100% !important;

    .ant-input-number-input {
      height: 44px !important;
      font-size: 0.875rem !important;
      line-height: 1.5 !important;
    }
  }

  .ant-input:focus,
  .ant-select-focused .ant-select-selector,
  .ant-picker-focused,
  .ant-input-number:focus {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.1) !important;
  }

  .ant-form-item-required::before {
    color: #ff4d4f !important;
    font-size: 0.875rem !important;
  }

  .ant-input[disabled] {
    background-color: #f5f5f5 !important;
    color: #999 !important;
  }

  .ant-select-disabled .ant-select-selector {
    background-color: #f5f5f5 !important;
    color: #999 !important;
  }
`;

const RepairModal = ({ visible, onClose, repair = null, onSuccess }) => {
  const { isMobile } = useBreakpoints();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isEditing = !!repair;
  const completedValue = Form.useWatch("completed", form);
  const assignedToValue = Form.useWatch("assignedTo", form);

  const safeToDate = safeToDateValue;

  // Load units data
  const { data: units = [] } = useQuery({
    queryKey: ["units", user?.uid],
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Load handymen data
  const { data: handymen = [] } = useQuery({
    queryKey: ["handymen", user?.uid],
    queryFn: () => HandymanModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Find tenant chat associated with this repair ticket
  const { data: tenantChat } = useQuery({
    queryKey: ["tenantChat", repair?.tenantId, user?.uid],
    queryFn: async () => {
      if (!repair?.tenantId || !user?.uid) return null;
      try {
        const allChats = await chatModel.getLandlordChats(user.uid);
        // Find the chat by tenantId (the regular tenant chat, not a maintenance chat)
        const chat = allChats.find((c) => c.tenantId === repair.tenantId);
        return chat || null;
      } catch (error) {
        console.error("Error finding tenant chat:", error);
        return null;
      }
    },
    enabled: !!isEditing && !!repair?.tenantId && !!user?.uid,
  });

  // Set form values when editing
  useEffect(() => {
    if (isEditing && repair) {
      form.setFieldsValue({
        title: repair.title,
        unitNumber: repair.unitNumber,
        category: repair.category,
        urgency: repair.urgency,
        priority: repair.priority,
        assignedTo: repair.assignedTo,
        scheduledDate: toDayjsValue(repair.scheduledDate),
        completed: (repair.status || "").toLowerCase() === "completed",
        estimatedCost: repair.estimatedCost,
        description: repair.description,
      });
    } else {
      form.resetFields();
    }
  }, [isEditing, repair, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const normalizedScheduledDate = safeToDate(values.scheduledDate);
      const completedFlag = !!values.completed;
      // Derive status priority: completed > scheduled > open
      let derivedStatus = isEditing ? repair.status : "open";
      if (completedFlag) {
        derivedStatus = "completed";
      } else if (normalizedScheduledDate) {
        derivedStatus = "scheduled";
      } else if (
        isEditing &&
        ["scheduled", "completed"].includes(repair.status)
      ) {
        derivedStatus = "open";
      }

      const repairData = {
        ...values,
        scheduledDate: normalizedScheduledDate,
        status: derivedStatus,
        completedDate: completedFlag ? new Date() : null,
        createdAt: isEditing ? repair.createdAt : new Date(),
        updatedAt: new Date(),
      };

      // Handle assignedTo: if it's undefined or empty string, set it to null to explicitly unassign
      if (repairData.assignedTo === undefined || repairData.assignedTo === "") {
        repairData.assignedTo = null;
      }

      // Remove undefined values to prevent Firebase errors (but keep null for assignedTo)
      const cleanedRepairData = Object.fromEntries(
        Object.entries(repairData).filter(([key, value]) => {
          // Keep assignedTo even if null (to allow unassignment)
          if (key === "assignedTo") return true;
          return value !== undefined;
        })
      );

      if (isEditing) {
        await RepairModel.update(repair.id, cleanedRepairData);
        message.success("Repair updated successfully");
        trackEvent("repair_updated", { repairId: repair.id });
      } else {
        await RepairModel.create(cleanedRepairData);
        message.success("Repair created successfully");
        trackEvent("repair_created");
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["repairs", user?.uid] });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving repair:", error);
      message.error(
        isEditing ? "Failed to update repair" : "Failed to create repair"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleViewChat = () => {
    if (tenantChat) {
      trackEvent("repair_chat_opened", {
        repairId: repair.id,
        chatId: tenantChat.id,
        tenantId: repair.tenantId,
      });
      navigate("/communication", {
        state: { selectChatId: tenantChat.id, repairId: repair.id },
      });
      onClose();
    } else if (repair?.tenantId) {
      // If chat doesn't exist yet, navigate to tenant chat URL (it will be created automatically)
      trackEvent("repair_chat_opened", {
        repairId: repair.id,
        tenantId: repair.tenantId,
      });
      navigate(`/communication/${repair.tenantId}`, {
        state: { repairId: repair.id },
      });
      onClose();
    }
  };

  const categoryOptions = [
    { value: "plumbing", label: "Plumbing" },
    { value: "electrical", label: "Electrical" },
    { value: "hvac", label: "HVAC" },
    { value: "appliances", label: "Appliances" },
    { value: "structural", label: "Structural" },
    { value: "cosmetic", label: "Cosmetic" },
    { value: "safety", label: "Safety" },
    { value: "cleaning_turnover", label: "Cleaning (Turnover)" },
    { value: "landscaping", label: "Landscaping" },
    { value: "tuckpointing_masonry", label: "Tuckpointing / Masonry" },
    { value: "painting", label: "Painting" },
    { value: "snow_removal", label: "Snow Removal" },
    { value: "other", label: "Other" },
  ];

  const urgencyOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  return (
    <StyledModal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "white",
          }}
        >
          <ToolOutlined
            style={{ fontSize: "1.5rem", filter: "brightness(0) invert(1)" }}
          />
          {isEditing ? "Edit Repair" : "Create New Repair Request"}
        </div>
      }
      destroyOnClose
      width={820}
      centered
    >
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ padding: "1.5rem" }}
      >
        <SectionTitle>
          <ToolOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Basic Information
        </SectionTitle>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="title"
              label="Repair Title"
              rules={[{ required: true, message: "Please enter a title" }]}
            >
              <Input placeholder="e.g., Leaky Kitchen Faucet" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="unitNumber"
              label={
                <span>
                  Door
                  <Tooltip title="Which door needs the repair?">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true, message: "Please select a door" }]}
            >
              <Select
                showSearch
                placeholder="Select door"
                filterOption={(input, option) =>
                  (option?.children?.toString() || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                optionLabelProp="label"
              >
                {units.map((unit) => {
                  const line1 =
                    unit.addressLine1 ||
                    getUnitDisplayAddress(unit) ||
                    unit.street ||
                    "";
                  const city = unit.city || "";
                  const state = unit.state || "";
                  const zip = unit.zip || unit.postalCode || "";
                  const address = [
                    line1,
                    [city, state].filter(Boolean).join(", "),
                    zip,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim();
                  const title = `Door ${unit.unitNumber}`;
                  const fullAddress =
                    address || unit.propertyName || "Property";
                  const displayText = address
                    ? `${title}  -  ${address}`
                    : `${title}  -  ${unit.propertyName || "Property"}`;

                  return (
                    <Option
                      key={unit.id}
                      value={unit.unitNumber}
                      label={displayText}
                      title={fullAddress}
                    >
                      <Tooltip
                        title={fullAddress}
                        placement="right"
                        mouseEnterDelay={0.5}
                      >
                        <div style={{ padding: "4px 0" }}>{displayText}</div>
                      </Tooltip>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <SectionTitle
            style={{ marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}
          >
            <FileTextOutlined
              style={{ marginRight: "8px", color: "var(--color-primary)" }}
            />{" "}
            Details
          </SectionTitle>
          {isEditing && (
            <Form.Item
              name="completed"
              valuePropName="checked"
              style={{ margin: 0 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontSize: "0.9rem",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {isMobile ? "Completed" : "Mark as Completed"}
                </span>
                <Switch
                  checked={!!completedValue}
                  onChange={(checked) =>
                    form.setFieldsValue({ completed: checked })
                  }
                />
              </div>
            </Form.Item>
          )}
        </div>
        <div
          style={{
            borderBottom: "2px solid var(--color-border-light)",
            marginTop: "0.5rem",
            marginBottom: "0.75rem",
          }}
        />
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select placeholder="Select category" options={categoryOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="urgency"
              label="Urgency"
              rules={[{ required: true, message: "Please select urgency" }]}
            >
              <Select placeholder="Select urgency" options={urgencyOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true, message: "Please select priority" }]}
            >
              <Select placeholder="Select priority" options={priorityOptions} />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>
          <ToolOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Assign to Service Team
        </SectionTitle>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="assignedTo"
              label={
                <span>
                  Assign to Service Team
                  <Tooltip title="Select a service team member to assign this repair to, or clear to remove assignment">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
            >
              <Select
                placeholder="Select service team member"
                allowClear
                clearIcon={
                  <span
                    style={{
                      color: "#ff4d4f",
                      position: "absolute",
                      right: "0px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    ✕
                  </span>
                }
              >
                {handymen.map((handyman) => (
                  <Option key={handyman.id} value={handyman.id}>
                    {handyman.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="scheduledDate"
              label={
                <span>
                  Scheduled Date
                  <Tooltip title="When should this repair be completed?">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
            >
              <DatePicker placeholder="Select date" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="estimatedCost"
              label={
                <span>
                  Estimated Cost
                  <Tooltip title="Estimated cost for this repair">
                    <InfoIcon />
                  </Tooltip>
                </span>
              }
            >
              <InputNumber
                placeholder="0.00"
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                onKeyPress={(e) => {
                  if (
                    !/[0-9.]/.test(e.key) &&
                    ![
                      "Backspace",
                      "Delete",
                      "Tab",
                      "Enter",
                      "ArrowLeft",
                      "ArrowRight",
                    ].includes(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {assignedToValue && (
          <>
            <div
              style={{
                marginTop: "1rem",
                marginBottom: "0.5rem",
                padding: "1rem",
                backgroundColor: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderRadius: "8px",
                borderLeft: "4px solid #0ea5e9",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                }}
              >
                <InfoCircleOutlined
                  style={{
                    color: "#0ea5e9",
                    fontSize: "1.1rem",
                    marginTop: "2px",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#0c4a6e",
                      marginBottom: "0.5rem",
                      fontSize: "0.9rem",
                    }}
                  >
                    Privacy Notice
                  </div>
                  <div
                    style={{
                      color: "#075985",
                      fontSize: "0.875rem",
                      lineHeight: "1.5",
                    }}
                  >
                    This service team member will be added to the tenant's chat
                    conversation. They may be able to see information shared
                    between you and the tenant, including messages, property
                    details, and maintenance history. You can disable a service
                    team member's access to specific conversations at any time.
                  </div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
              <Button
                type="default"
                danger
                icon={<CloseOutlined />}
                onClick={async () => {
                  try {
                    setLoading(true);
                    form.setFieldsValue({ assignedTo: undefined });
                    if (isEditing && repair?.id) {
                      await RepairModel.unassignHandyman(repair.id);
                      message.success(
                        "Service team member removed successfully"
                      );
                      trackEvent("handyman_unassigned", {
                        repairId: repair.id,
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["repairs", user?.uid],
                      });
                      onSuccess?.();
                    }
                  } catch (error) {
                    console.error("Error removing handyman:", error);
                    message.error("Failed to remove service team member");
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ width: "100%" }}
                loading={loading}
              >
                Remove Service Team Member
              </Button>
            </div>
          </>
        )}

        <SectionTitle>
          <FileTextOutlined
            style={{ marginRight: "8px", color: "var(--color-primary)" }}
          />{" "}
          Description
        </SectionTitle>
        <Form.Item
          name="description"
          rules={[
            { required: true, message: "Please enter a description" },
            { min: 10, message: "Description must be at least 10 characters" },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Describe the repair issue in detail..."
            style={{
              minHeight: "80px",
              maxHeight: "120px",
              resize: "vertical",
            }}
          />
        </Form.Item>

        {isEditing && repair?.tenantId && (
          <div style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>
            <Button
              type="default"
              icon={<MessageOutlined />}
              onClick={handleViewChat}
              style={{ width: "100%" }}
              loading={!tenantChat && !!repair?.tenantId}
            >
              View Chat Conversation
            </Button>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isEditing && !isMobile ? "space-between" : "center",
            gap: "0.75rem",
            alignItems: "stretch",
            marginTop: "1.5rem",
          }}
        >
          {isEditing && (
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <Button
                danger
                onClick={() => {
                  Modal.confirm({
                    title: "Delete Repair Ticket",
                    content:
                      "Are you sure you want to delete this repair ticket? This action cannot be undone.",
                    okText: "Delete",
                    okType: "danger",
                    cancelText: "Cancel",
                    onOk: async () => {
                      try {
                        setLoading(true);
                        await RepairModel.delete(repair.id);
                        message.success("Repair ticket deleted successfully");
                        trackEvent("repair_deleted", { repairId: repair.id });

                        // Invalidate queries to refresh data
                        queryClient.invalidateQueries({
                          queryKey: ["repairs", user?.uid],
                        });

                        onSuccess?.();
                        onClose();
                      } catch (error) {
                        console.error("Error deleting repair:", error);
                        message.error("Failed to delete repair ticket");
                      } finally {
                        setLoading(false);
                      }
                    },
                  });
                }}
                style={isMobile ? { flex: 1 } : {}}
              >
                Delete
              </Button>
              <Button
                onClick={handleCancel}
                style={isMobile ? { flex: 1 } : {}}
              >
                Cancel
              </Button>
            </div>
          )}
          {!isEditing && (
            <Button
              onClick={handleCancel}
              style={isMobile ? { width: "100%" } : {}}
            >
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{
              width: isMobile ? "100%" : isEditing ? "auto" : "auto",
              marginLeft: !isMobile && isEditing ? "auto" : "0",
            }}
          >
            {isEditing ? "Save Changes" : "Create Repair"}
          </Button>
        </div>
      </StyledForm>
    </StyledModal>
  );
};

export default RepairModal;
